import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  RoomProvider,
  useRoomContext,
  useRoomSession,
  useRoomParticipants,
  useRoomPhase,
  useLiveStateSync,
  type GameMode,
  type EvaluationExam,
  validateExam,
  programExamAccessCode,
} from '@interactive-room';
import { SalaShell } from '../components/layout/SalaShell';
import { JoinCodeDisplay } from '../components/ui/JoinCodeDisplay';
import { ActivitySwitcherSheet } from '../components/ui/ActivitySwitcherSheet';
import { ActivityFrame } from '../components/ui/ActivityFrame';
import type { ActivityPreset } from '@interactive-room/catalog/activityPresets';
import { ContextualHostBar } from '../components/ui/ContextualHostBar';
import { CollapsedParticipants } from '../components/ui/CollapsedParticipants';
import { JoinToastStack } from '../components/ui/JoinToastStack';
import { ExamMonitorPanel } from '../components/ui/ExamMonitorPanel';
import { ExamDesarrolloReviewPanel } from '../components/ui/ExamDesarrolloReviewPanel';
import { ExamLaunchSheet } from '../components/ui/ExamLaunchSheet';
import { useJoinToasts } from '../hooks/useJoinToasts';
import { MODE_VISUALS } from '../theme/designSystem';
import type { ActivityDraft } from '../studio/activityStorage';
import { getActivityDraft } from '../studio/activityStorage';
import { getEvaluationExam } from '../studio/examStorage';
import {
  clearPendingHostLaunch,
  loadPendingHostLaunch,
  parseHostLaunchFromSearch,
  savePendingHostLaunch,
  type HostLaunchIntent,
} from '../studio/hostLaunch';

function HostCreateForm({
  hostId,
  setHostId,
  onCreate,
  pendingLaunch,
  subjectId,
}: {
  hostId: string;
  setHostId: (v: string) => void;
  onCreate: () => void;
  pendingLaunch: HostLaunchIntent | null;
  subjectId?: string;
}) {
  return (
    <div className="flex min-h-[calc(100dvh-8rem)] items-center justify-center">
      <div className="sala-bento w-full max-w-4xl">
        <div className="col-span-12 lg:col-span-7">
          {subjectId && (
            <Link to={`/studio/asignatura/${subjectId}`} className="sala-label text-[var(--sala-accent-neon)]">
              ← Volver a Studio
            </Link>
          )}
          <p className="sala-kicker mb-4 mt-2">Sala digital · Consola docente</p>
          <h1 className="font-display text-4xl font-extrabold leading-none sm:text-5xl">
            {pendingLaunch ? (
              <>
                Listo para
                <br />
                <span style={{ color: 'var(--sala-accent-neon)' }}>transmitir</span>
              </>
            ) : (
              <>
                Nueva
                <br />
                <span style={{ color: 'var(--sala-accent-neon)' }}>sesión</span>
              </>
            )}
          </h1>
          <p className="mt-4 max-w-md text-[var(--sala-ink-muted)]">
            {pendingLaunch
              ? `Al crear la sala, cargaremos «${pendingLaunch.title}» y podrás proyectarla al instante.`
              : 'Crea una sala, comparte el código y lanza actividades en vivo. Para preparar contenido, empieza en Studio.'}
          </p>
          {!pendingLaunch && (
            <Link to="/studio" className="sala-btn-outline mt-4 inline-flex text-xs">
              Ir a Studio (preparar recursos)
            </Link>
          )}
        </div>

        <div className="col-span-12 lg:col-span-5">
          <div className="sala-glass-raised space-y-5 p-6">
            {pendingLaunch && (
              <div className="rounded-xl border border-[var(--sala-border-strong)] bg-[var(--sala-surface)] p-4">
                <p className="sala-label">Desde Studio</p>
                <p className="mt-1 font-display text-lg font-bold">{pendingLaunch.title}</p>
                <p className="mt-1 text-xs text-[var(--sala-ink-dim)]">
                  {pendingLaunch.kind === 'exam' ? 'Evaluación mixta' : 'Actividad en vivo'}
                </p>
              </div>
            )}
            <label className="block">
              <span className="sala-label">Identificador del profesor</span>
              <input
                className="sala-input-dark mt-2"
                value={hostId}
                onChange={(e) => setHostId(e.target.value)}
                placeholder="prof_garcia"
              />
            </label>
            <button type="button" className="sala-btn-hero w-full" onClick={onCreate}>
              {pendingLaunch ? 'Abrir sala digital y cargar recurso' : 'Abrir sala digital'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function HostContent() {
  const [searchParams] = useSearchParams();
  const [hostId, setHostId] = useState(() => {
    try {
      return localStorage.getItem('sala_host_id') || '';
    } catch {
      return '';
    }
  });
  const [selectedMode, setSelectedMode] = useState<GameMode | null>(null);
  const [activitySheetOpen, setActivitySheetOpen] = useState(false);
  const [stagedDraftId, setStagedDraftId] = useState<string | null>(null);
  const [stagedTitle, setStagedTitle] = useState<string | null>(null);
  const [examLaunchOpen, setExamLaunchOpen] = useState(false);
  const launchAppliedRef = useRef(false);

  const pendingLaunch = useMemo(() => {
    const fromUrl = parseHostLaunchFromSearch(searchParams);
    if (fromUrl) {
      if (fromUrl.kind === 'activity') {
        const draft = getActivityDraft(fromUrl.resourceId);
        if (draft) return { ...fromUrl, title: draft.title };
      } else {
        const exam = getEvaluationExam(fromUrl.resourceId);
        if (exam) return { ...fromUrl, title: exam.title };
      }
    }
    return loadPendingHostLaunch();
  }, [searchParams]);

  const subjectId = pendingLaunch?.subjectId ?? searchParams.get('subject') ?? undefined;

  const { connected, joinCode, roomId, createRoomAsHost, lastError } = useRoomSession();
  const { participants, connectedCount } = useRoomParticipants();
  const { phase, setPhase } = useRoomPhase();
  const {
    setActivity,
    setRouting,
    dispatchActivityAction,
    rejoinAsHost,
    state,
    unlockParticipant,
    launchExam,
    advanceExamBlock,
    endExam,
    confirmExamReview,
  } = useRoomContext();

  const examSession = state.state?.examSession ?? null;
  const activeMode = (state.state?.core.activity.mode as GameMode | undefined) ?? selectedMode;
  const assessmentMode = Boolean(state.state?.core.flags?.assessmentMode);
  const hasActivity = Boolean(state.state?.payload && state.state?.runtime);
  const { toasts, dismiss } = useJoinToasts(participants);

  useLiveStateSync(Boolean(roomId));

  useEffect(() => {
    const fromUrl = parseHostLaunchFromSearch(searchParams);
    if (!fromUrl) return;
    if (fromUrl.kind === 'activity') {
      const draft = getActivityDraft(fromUrl.resourceId);
      if (draft) savePendingHostLaunch({ ...fromUrl, title: draft.title });
    } else {
      const exam = getEvaluationExam(fromUrl.resourceId);
      if (exam) savePendingHostLaunch({ ...fromUrl, title: exam.title });
    }
  }, [searchParams]);

  useEffect(() => {
    if (!roomId && connected) {
      void rejoinAsHost();
    }
  }, [connected, roomId, rejoinAsHost]);

  const handleCreate = () => {
    const id = hostId.trim() || `prof_${Date.now().toString(36).slice(-6)}`;
    try {
      localStorage.setItem('sala_host_id', id);
    } catch {
      /* ignore */
    }
    void createRoomAsHost(id, 'Sesión en vivo');
  };

  const browseMode = (mode: GameMode) => {
    setSelectedMode(mode);
  };

  const stageDraft = (draft: ActivityDraft) => {
    setSelectedMode(draft.mode);
    setStagedDraftId(draft.id);
    setStagedTitle(draft.title);
    void setActivity(draft.mode, draft.payload as never, { subjectTag: draft.subjectTag });
  };

  const stagePreset = (preset: ActivityPreset) => {
    setSelectedMode(preset.mode);
    setStagedDraftId(null);
    setStagedTitle(preset.title);
    void setActivity(preset.mode, preset.payload() as never, { subjectTag: preset.subjectTag });
  };

  const launchStaged = () => {
    setActivitySheetOpen(false);
    if (phase === 'LOBBY') {
      void setPhase('PREPARING');
    }
  };

  const handleLaunchExam = (raw: EvaluationExam) => {
    const exam = programExamAccessCode(validateExam(raw));
    void launchExam(exam);
    void setPhase('ACTIVE');
  };

  useEffect(() => {
    if (!roomId || !pendingLaunch || launchAppliedRef.current) return;

    if (pendingLaunch.kind === 'activity') {
      const draft = getActivityDraft(pendingLaunch.resourceId);
      if (!draft) return;
      setSelectedMode(draft.mode);
      setStagedDraftId(draft.id);
      setStagedTitle(draft.title);
      void setActivity(draft.mode, draft.payload as never, { subjectTag: draft.subjectTag });
      launchAppliedRef.current = true;
      clearPendingHostLaunch();
      if (phase === 'LOBBY') void setPhase('PREPARING');
      return;
    }

    const exam = getEvaluationExam(pendingLaunch.resourceId);
    if (!exam) return;
    handleLaunchExam(exam);
    launchAppliedRef.current = true;
    clearPendingHostLaunch();
  }, [roomId, pendingLaunch, phase, setPhase, setActivity, launchExam]);

  const examHasNext =
    examSession?.status === 'ACTIVE' &&
    examSession.advanceMode === 'HOST' &&
    examSession.currentBlockIndex + 1 < examSession.totalBlocks;

  const examActive = examSession?.status === 'ACTIVE';

  return (
    <SalaShell variant="cockpit" phase={phase} mode={activeMode}>
      <JoinToastStack toasts={toasts} onDismiss={dismiss} />

      {!roomId ? (
        <HostCreateForm
          hostId={hostId}
          setHostId={setHostId}
          onCreate={handleCreate}
          pendingLaunch={pendingLaunch}
          subjectId={subjectId}
        />
      ) : (
        <div className="flex h-[calc(100dvh-4.5rem)] flex-col gap-2 overflow-hidden">
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {joinCode && <JoinCodeDisplay code={joinCode} connectedCount={connectedCount} compact />}
            <span
              className="sala-kicker"
              style={{ color: connected ? '#34d399' : 'var(--sala-ink-dim)' }}
            >
              <span
                className="h-1.5 w-1.5 rounded-full"
                style={{ background: connected ? '#34d399' : 'var(--sala-ink-dim)' }}
              />
              {connected ? 'En línea' : 'Conectando…'}
            </span>
            {assessmentMode && (
              <span className="rounded-full border border-emerald-500/40 bg-emerald-950/30 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-emerald-300">
                🔒 Integridad activa
              </span>
            )}
            {examActive && (
              <span className="rounded-full border border-[#6A4C9333] bg-[#f5f0ff22] px-2 py-0.5 text-[9px] font-bold text-[#b794f6]">
                {examSession.advanceMode === 'AUTONOMOUS' ? 'Eval autónoma' : 'Eval sincronizada'}
              </span>
            )}
            {activeMode && (
              <span
                className="ml-auto font-display text-[10px] font-bold uppercase tracking-widest"
                style={{ color: MODE_VISUALS[activeMode].neon }}
              >
                {MODE_VISUALS[activeMode].verb}
              </span>
            )}
          </div>

          <ContextualHostBar
            phase={phase}
            mode={activeMode}
            payload={state.state?.payload}
            runtime={state.state?.runtime}
            connectedCount={connectedCount}
            hasActivity={hasActivity || examActive}
            setPhase={setPhase}
            dispatchActivityAction={dispatchActivityAction}
            extraActions={
              <>
                <button
                  type="button"
                  className="sala-btn-outline py-2 text-[10px]"
                  onClick={() => setExamLaunchOpen(true)}
                >
                  {examActive ? 'Cambiar evaluación' : 'Evaluación mixta'}
                </button>
                {examHasNext && (
                  <button
                    type="button"
                    className="sala-btn-hero py-2 text-[10px]"
                    onClick={() => void advanceExamBlock()}
                  >
                    Siguiente bloque ({examSession!.currentBlockIndex + 2}/{examSession!.totalBlocks})
                  </button>
                )}
                {examActive && (
                  <button
                    type="button"
                    className="sala-btn-outline py-2 text-[10px] text-amber-300"
                    onClick={() => void endExam()}
                  >
                    Finalizar evaluación
                  </button>
                )}
                {!examActive && (
                  <button
                    type="button"
                    className="sala-btn-outline py-2 text-[10px]"
                    onClick={() => setActivitySheetOpen(true)}
                  >
                    Actividades en vivo
                  </button>
                )}
                <button
                  type="button"
                  className="sala-btn-outline py-2 text-[10px]"
                  onClick={() => void setRouting('TEAMS_RANDOM', 4)}
                >
                  Equipos
                </button>
                {joinCode && (
                  <a
                    href={`/projector/${joinCode}`}
                    target="_blank"
                    rel="noreferrer"
                    className="sala-btn-hero py-2 text-[10px]"
                  >
                    Proyector ↗
                  </a>
                )}
              </>
            }
          />

          <div className="relative min-h-0 flex-1">
            <CollapsedParticipants
              participants={participants}
              connectedCount={connectedCount}
              assessmentMode={assessmentMode}
              onUnlock={(id) => void unlockParticipant(id)}
            />
            <div className="h-full min-h-0">
              {examActive && examSession?.advanceMode === 'AUTONOMOUS' ? (
                <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-[var(--sala-border)] p-8">
                  <div className="max-w-md text-center">
                    <p className="font-display text-xl font-bold">{examSession.title}</p>
                    <p className="mt-2 text-sm text-[var(--sala-ink-muted)]">
                      Modo autónomo — los alumnos avanzan a su ritmo. Usa el monitor para ver progreso.
                    </p>
                  </div>
                </div>
              ) : (
                <ActivityFrame role="host" phase={phase} mode={activeMode} fullscreen />
              )}
            </div>
            <ExamMonitorPanel
              examSession={examSession}
              participants={participants}
              onUnlock={(id) => void unlockParticipant(id)}
            />
            <ExamDesarrolloReviewPanel
              examSession={examSession}
              onConfirm={(pid, bid) => void confirmExamReview(pid, bid)}
            />
          </div>

          <ExamLaunchSheet
            open={examLaunchOpen}
            onOpenChange={setExamLaunchOpen}
            onLaunch={handleLaunchExam}
            subjectId={subjectId}
          />

          <ActivitySwitcherSheet
            open={activitySheetOpen}
            onOpenChange={setActivitySheetOpen}
            activeMode={activeMode}
            phase={phase}
            stagedDraftId={stagedDraftId}
            stagedTitle={stagedTitle}
            subjectId={subjectId}
            onBrowseMode={browseMode}
            onStageDraft={stageDraft}
            onStagePreset={stagePreset}
            onLaunchStaged={launchStaged}
          />

          {lastError && (
            <p className="shrink-0 rounded-xl border border-red-500/30 bg-red-950/40 px-4 py-2 text-sm text-red-300">
              {lastError}
            </p>
          )}
        </div>
      )}
    </SalaShell>
  );
}

export function HostPage() {
  return (
    <RoomProvider>
      <HostContent />
    </RoomProvider>
  );
}
